import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatRupiah } from "../lib/format";
import { Printer, Plus, FileText, AlertTriangle, Lock, RefreshCw, Download, Pencil, Trash2, Wallet } from "lucide-react";
import { parseLocaleNum, errMsg } from "../lib/locale";
import { toast } from "sonner";
import { CatatTransaksiModal, TotalanModal, DetailTxModal, fmtQty } from "../components/VendorModals";
import { PreviewNotaModal } from "../components/PreviewNotaModal";
import { DateRangeModal } from "../components/DateRangeModal";
import { ErrorBoundary } from "../components/ErrorBoundary";

export default function VendorDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [vendor, setVendor] = useState(null);
    const [plafon, setPlafon] = useState(null);
    const [jurnal, setJurnal] = useState(null);
    const [bons, setBons] = useState([]);
    const [rejects, setRejects] = useState([]);
    const [totalans, setTotalans] = useState([]);
    const [kasbons, setKasbons] = useState([]);
    const [setorans, setSetorans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("jurnal");
    const [showCatat, setShowCatat] = useState(false);
    const [catatJenis, setCatatJenis] = useState("pengambilan");
    const [showCatatMenu, setShowCatatMenu] = useState(false);
    const [showTotalan, setShowTotalan] = useState(false);
    const [detailTx, setDetailTx] = useState(null);
    const [preview, setPreview] = useState(null); // { title, url, filename }
    const [rangeModal, setRangeModal] = useState(null); // null | "pdf" | "excel"
    const [editTx, setEditTx] = useState(null); // {jenis, id, nomor}
    const [delTx, setDelTx] = useState(null); // {jenis, id, nomor}

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const [v, p, j, bk, bp, rj, tt, kt, sr] = await Promise.all([
                api.get(`/vendors/${id}`),
                api.get(`/vendors/${id}/plafon-v2`),
                api.get(`/vendors/${id}/jurnal?periode=semua`),
                api.get(`/bon-kain?vendor_id=${id}`),
                api.get(`/bon-peralatan?vendor_id=${id}`),
                api.get(`/reject?vendor_id=${id}`),
                api.get(`/totalan?vendor_id=${id}`),
                api.get(`/vendors/${id}/kasbon-tunai`),
                api.get(`/vendors/${id}/setoran`),
            ]);
            setKasbons(kt.data || []);
            setSetorans(sr.data || []);
            setVendor(v.data);
            setPlafon(p.data);
            setJurnal(j.data);
            const allBons = [
                ...(bk.data || []).map((b) => ({ ...b, _jenis: "bon_kain" })),
                ...(bp.data || []).map((b) => ({ ...b, _jenis: "bon_peralatan" })),
            ];
            const totRaw = tt.data || [];
            setBons(allBons.map((b) => {
                let pot = 0;
                for (const t of totRaw) for (const a of (t.alokasi_potongan_bon || [])) if (a.bon_id === b.id && a.bon_jenis === b._jenis) pot += Number(a.nilai_potongan || 0);
                return { ...b, sisa_bon: (b.total_nilai || 0) - pot };
            }));
            setRejects(rj.data || []);
            setTotalans(totRaw);
        } catch (e) { setErr(e.response?.data?.detail || "Gagal memuat"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    useEffect(() => {
        window.__crumb_vendor_name_ = vendor?.nama || "";
        return () => { window.__crumb_vendor_name_ = ""; };
    }, [vendor?.nama]);

    const rejectTerbuka = useMemo(() => rejects.filter((r) => r.jenis === "A_bisa_diperbaiki" && r.status === "menunggu"), [rejects]);
    const bonTerbuka = useMemo(() => bons.filter((b) => Math.abs(b.sisa_bon || 0) > 0.01), [bons]);
    const hakVendor = plafon?.sisa_hak || 0;
    const sisaTotalan = plafon?.sisa_totalan || 0;
    const countBelumLunas = plafon?.count_totalan_belum_lunas || 0;
    const kasbonTunai = plafon?.kasbon_tunai_outstanding || 0;
    const countKasbonAktif = plafon?.count_kasbon_aktif || 0;
    const canInput = Boolean(user); // v1.3.0 RBAC: semua authenticated boleh catat transaksi

    const openCatat = (jenis) => { setCatatJenis(jenis); setShowCatat(true); setShowCatatMenu(false); };
    const doDeleteTx = async () => {
        const t = delTx; setDelTx(null);
        try {
            await api.delete(`${DELETE_URL[t.jenis]}${t.id}`);
            toast.success(`${t.nomor || "Transaksi"} dipindahkan ke Riwayat Terhapus`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
    };
    const cetakBuku = () => setRangeModal("pdf");
    const exportExcel = () => setRangeModal("excel");

    const handleRangeConfirm = async ({ dari, sampai }) => {
        setRangeModal(null);
        const nama = (vendor?.nama || "vendor").replace(/\s+/g, "_");
        if (rangeModal === "pdf") {
            setPreview({
                title: `Buku Vendor ${vendor?.nama || ""} — ${dari} s/d ${sampai}`,
                url: `/vendors/${id}/buku-vendor/pdf?dari=${dari}&sampai=${sampai}`,
                filename: `Buku_${nama}_${dari}_${sampai}.pdf`,
            });
        } else {
            // Export Excel: download langsung lewat fetch (auth header)
            try {
                const token = localStorage.getItem("bvg_token");
                const r = await fetch(`${api.defaults.baseURL}/vendors/${id}/jurnal/excel?dari=${dari}&sampai=${sampai}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!r.ok) throw new Error(await r.text().catch(() => `HTTP ${r.status}`));
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Jurnal_${nama}_${dari}_${sampai}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1500);
                toast.success("Excel berhasil di-export");
            } catch (e) {
                toast.error("Gagal export Excel: " + (e.message || "unknown"));
            }
        }
    };

    if (err && !vendor) {
        return (
            <div className="p-6 max-w-5xl mx-auto">
                <div className="panel border-red-200 bg-red-50 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-4 h-4" />{err}</div>
                    <button onClick={load} className="text-red-700 font-medium flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Coba Lagi</button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Oripio header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div
                        className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-lg md:text-xl"
                        style={{ background: "linear-gradient(135deg, var(--apple-blue), var(--apple-blue-hover))" }}
                        data-testid="vendor-icon"
                    >
                        {(vendor?.nama || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl md:text-2xl font-semibold tracking-tight" style={{ color: "var(--text-primary)", letterSpacing: "-0.3px" }} data-testid="vendor-nama">
                                {vendor?.nama || "..."}
                            </h1>
                            <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{vendor?.kode_vendor}</span>
                            {vendor && <span className={`badge ${vendor.status === "aktif" ? "badge-hijau" : "badge-abu"}`}>{vendor.status}</span>}
                        </div>
                        <div className="text-xs md:text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                            {vendor?.no_hp || "—"}
                            {vendor?.nama_bank ? ` · ${vendor.nama_bank}` : ""}
                            {vendor?.no_rekening ? ` ${vendor.no_rekening}` : ""}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {canInput && (
                        <div className="relative">
                            <button onClick={() => setShowCatatMenu((s) => !s)} data-testid="btn-catat-tx" className="pill-primary">
                                <Plus className="w-4 h-4" /> Catat Transaksi
                            </button>
                            {showCatatMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowCatatMenu(false)} />
                                    <div className="absolute z-20 top-full right-0 mt-1 panel min-w-[220px] py-1" data-testid="catat-tx-menu">
                                        {[["pengambilan", "Pengambilan Kain"], ["bahan_penolong", "Bahan Penolong"], ["setoran", "Setoran"], ["kasbon", "Kasbon"], ["rijek", "Rijek"], ["setor_perbaikan", "Setor Perbaikan"]].map(([k, l]) => (
                                            <button key={k} onClick={() => openCatat(k)} data-testid={`catat-menu-${k}`} className="w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--bg-subtle)]">{l}</button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <button onClick={() => setShowTotalan(true)} data-testid="btn-totalan"
                        className="px-4 py-2 rounded-full font-medium text-sm flex items-center gap-1.5"
                        style={{ border: "1px solid var(--apple-blue)", color: "var(--apple-blue)", background: "#fff" }}>
                        <FileText className="w-4 h-4" /> Buat Totalan
                    </button>
                    <button onClick={cetakBuku} data-testid="btn-cetak-buku"
                        className="px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-1.5"
                        style={{ border: "1px solid var(--border)", color: "var(--text-secondary)", background: "#fff" }}>
                        <Printer className="w-4 h-4" /> Cetak Buku
                    </button>
                    {canInput && (
                        <button onClick={exportExcel} data-testid="btn-export-excel"
                            className="px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-1.5"
                            style={{ border: "1px solid var(--border)", color: "var(--text-secondary)", background: "#fff" }}>
                            <Download className="w-4 h-4" /> Export Excel
                        </button>
                    )}
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 md:gap-3">
                <div className="rounded-xl bg-white p-3 md:p-4" style={{ border: "1px solid var(--border)" }}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
                        {hakVendor >= 0 ? "Hak Vendor" : "Hutang Vendor"}
                    </div>
                    <div className={`text-lg md:text-2xl font-semibold num-tabular tracking-tight ${hakVendor >= 0 ? "" : ""}`}
                        style={{ color: hakVendor >= 0 ? "var(--apple-blue-hover)" : "var(--apple-red)", letterSpacing: "-0.3px" }}
                        data-testid="vendor-hak">
                        {formatRupiah(Math.abs(hakVendor))}
                    </div>
                </div>
                <div className="rounded-xl bg-white p-3 md:p-4" style={{ border: "1px solid var(--border)" }}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Bon Pengambilan Barang</div>
                    <div className="text-lg md:text-2xl font-semibold num-tabular tracking-tight" style={{ color: "var(--text-primary)", letterSpacing: "-0.3px" }} data-testid="vendor-bon-tersisa">
                        {formatRupiah(bonTerbuka.reduce((s, b) => s + b.sisa_bon, 0))}
                    </div>
                </div>
                <div className="rounded-xl bg-white p-3 md:p-4" style={{ border: "1px solid var(--border)" }} data-testid="card-kasbon-tunai">
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Kasbon Tunai</div>
                    <div className="text-lg md:text-2xl font-semibold num-tabular tracking-tight"
                        style={{ color: kasbonTunai > 0.01 ? "#C76E00" : "var(--text-muted)", letterSpacing: "-0.3px" }}
                        data-testid="vendor-kasbon-tunai">
                        {formatRupiah(kasbonTunai)}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }} data-testid="vendor-kasbon-tunai-count">
                        {countKasbonAktif > 0 ? `${countKasbonAktif} kasbon aktif` : "Tidak ada kasbon aktif"}
                    </div>
                </div>
                <div className="rounded-xl bg-white p-3 md:p-4" style={{ border: "1px solid var(--border)" }}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Rijek Terbuka</div>
                    <div className="text-lg md:text-2xl font-semibold num-tabular tracking-tight" style={{ color: rejectTerbuka.length > 0 ? "var(--warn)" : "var(--text-primary)", letterSpacing: "-0.3px" }}>
                        {rejectTerbuka.length} <span className="text-xs font-normal">pcs</span>
                    </div>
                </div>
                <div className="rounded-xl bg-white p-3 md:p-4 col-span-2 md:col-span-1" style={{ border: "1px solid var(--border)" }} data-testid="card-sisa-totalan">
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Sisa Totalan</div>
                    <div className="text-lg md:text-2xl font-semibold num-tabular tracking-tight"
                        style={{ color: sisaTotalan > 0.01 ? "#C76E00" : "var(--text-muted)", letterSpacing: "-0.3px" }}
                        data-testid="vendor-sisa-totalan">
                        {formatRupiah(sisaTotalan)}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }} data-testid="vendor-sisa-totalan-count">
                        {countBelumLunas > 0 ? `${countBelumLunas} Totalan belum lunas` : "Lunas semua"}
                    </div>
                </div>
            </div>

            {/* v1.8.17 — Segmented control pill style (Apple/Oripio). Container abu muda,
                tab active bg putih + apple-blue text + shadow-sm. Mobile tetap scroll horizontal. */}
            <div className="inline-flex items-center gap-1 rounded-full p-1 overflow-x-auto hide-scrollbar max-w-full"
                style={{ background: "#F5F5F7" }}>
                {[["jurnal", "Jurnal"], ["setoran", "Setoran"], ["bonrijek", "Pengambilan & Rijek"], ["kasbon", "Kasbon Tunai"], ["totalan", "Totalan"]].map(([k, l]) => {
                    const isActive = tab === k;
                    const count = k === "bonrijek" ? (bonTerbuka.length + rejectTerbuka.length) : k === "kasbon" ? countKasbonAktif : k === "setoran" ? setorans.length : 0;
                    return (
                        <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                            className="px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-150 flex items-center gap-1.5"
                            style={{
                                background: isActive ? "#ffffff" : "transparent",
                                color: isActive ? "var(--apple-blue)" : "var(--text-secondary)",
                                boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.05)" : "none",
                            }}
                            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.5)"; }}
                            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
                            {l}
                            {count > 0 && (
                                <span className="text-[10px] font-semibold rounded-full px-1.5 py-0.5" data-testid={`tab-${k}-count`}
                                    style={{
                                        background: isActive ? "var(--apple-blue-soft)" : "rgba(255,159,10,0.15)",
                                        color: isActive ? "var(--apple-blue)" : "#C76E00",
                                    }}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {loading && <div className="panel p-6 text-center text-sm text-slate-400">Memuat...</div>}
            {!loading && !err && tab === "jurnal" && (
                <ErrorBoundary label="Jurnal Vendor" onReset={load}>
                    <JurnalTab entries={jurnal?.entries || []} onRowClick={setDetailTx} vendorId={id} vendorNama={vendor?.nama} onPreview={setPreview} onEdit={setEditTx} onDelete={setDelTx} />
                </ErrorBoundary>
            )}
            {!loading && !err && tab === "bonrijek" && (
                <ErrorBoundary label="Pengambilan &amp; Rijek" onReset={load}>
                    <BonRijekTab bons={bonTerbuka} rejects={rejectTerbuka} onReload={load} vendor={vendor} onEdit={setEditTx} onDelete={setDelTx} />
                </ErrorBoundary>
            )}
            {!loading && !err && tab === "setoran" && (
                <ErrorBoundary label="Setoran" onReset={load}>
                    <SetoranTab setorans={setorans} onEdit={setEditTx} onDelete={setDelTx} />
                </ErrorBoundary>
            )}
            {!loading && !err && tab === "kasbon" && (
                <ErrorBoundary label="Kasbon Tunai" onReset={load}>
                    <KasbonTab kasbons={kasbons} onEdit={setEditTx} onDelete={setDelTx} />
                </ErrorBoundary>
            )}
            {!loading && !err && tab === "totalan" && (
                <ErrorBoundary label="Totalan" onReset={load}>
                    <TotalanTab totalans={totalans} vendor={vendor} onReload={load} />
                </ErrorBoundary>
            )}

            {showCatat && vendor && (
                <ErrorBoundary label="Form Catat Transaksi" onReset={() => setShowCatat(false)}>
                    <CatatTransaksiModal vendor={vendor} jenis={catatJenis} onClose={() => setShowCatat(false)} onSaved={() => { setShowCatat(false); load(); }} bons={bons} rejects={rejectTerbuka} />
                </ErrorBoundary>
            )}
            {editTx && vendor && (
                <ErrorBoundary label="Form Edit Transaksi" onReset={() => setEditTx(null)}>
                    <CatatTransaksiModal mode="edit" existing={editTx} vendor={vendor} onClose={() => setEditTx(null)} onSaved={() => { setEditTx(null); load(); }} />
                </ErrorBoundary>
            )}
            {delTx && (
                <div className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center p-4" onClick={() => setDelTx(null)}>
                    <div className="panel p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="tx-delete-dialog">
                        <h3 className="font-bold mb-2" data-testid="tx-delete-title">{delTx.label ? `Hapus ${delTx.label} ${delTx.nomor || ""}?` : "Hapus Transaksi?"}</h3>
                        <div className="text-xs text-slate-600 mb-4">
                            {delTx.nomor || "Transaksi"} akan dipindahkan ke Riwayat Terhapus. 30 hari bisa dipulihkan.
                            {delTx.jenis === "reject" && " Rijek dan Bon Rijek B (RJB) pasangannya ikut terhapus sekaligus."}
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setDelTx(null)} data-testid="tx-delete-cancel" className="px-3 py-1.5 border border-slate-300 rounded text-sm">Batal</button>
                            <button onClick={doDeleteTx} data-testid="tx-delete-confirm" className="px-3 py-1.5 rounded text-sm font-medium text-white bg-red-600 hover:bg-red-700">Ya, Hapus</button>
                        </div>
                    </div>
                </div>
            )}
            {showTotalan && vendor && (
                <ErrorBoundary label="Form Buat Totalan" onReset={() => setShowTotalan(false)}>
                    <TotalanModal vendor={vendor} onClose={() => setShowTotalan(false)} onSaved={() => { setShowTotalan(false); load(); }} />
                </ErrorBoundary>
            )}
            {detailTx && (
                <ErrorBoundary label="Detail Transaksi" onReset={() => setDetailTx(null)}>
                    <DetailTxModal entry={detailTx} vendor={vendor} onClose={() => setDetailTx(null)} />
                </ErrorBoundary>
            )}
            <PreviewNotaModal
                open={!!preview}
                onClose={() => setPreview(null)}
                title={preview?.title || ""}
                url={preview?.url || ""}
                filename={preview?.filename || "nota.pdf"}
                share={preview?.share || null}
            />
            <DateRangeModal
                open={!!rangeModal}
                mode={rangeModal}
                onClose={() => setRangeModal(null)}
                onConfirm={handleRangeConfirm}
            />
        </div>
    );
}

const JENIS_LABEL_MAP = {
    "Pengambilan Kain": "PENGAMBILAN KAIN",
    "Bon Peralatan": "BON PERALATAN",
    "Bon Rijek B": "BON RIJEK B (BUYBACK)",
    "Setor Barang Jadi": "SETORAN BARANG",
    "Kasbon": "KASBON",
    "Rijek A": "RIJEK A",
    "Rijek B": "RIJEK B",
    "Setor Perbaikan": "SETOR PERBAIKAN",
    "Pembayaran Totalan": "PEMBAYARAN TOTALAN",
};
const JENIS_API_MAP = {
    "Pengambilan Kain": "bon_kain",
    "Bon Rijek B": "bon_kain",
    "Bon Peralatan": "bon_peralatan",
    "Setor Barang Jadi": "setor",
    "Kasbon": "kasbon",
    "Rijek A": "reject",
    "Rijek B": "reject",
    "Setor Perbaikan": "setor_perbaikan",
    "Pembayaran Totalan": "totalan",
};
const EDITABLE = ["bon_kain", "bon_peralatan", "setor", "kasbon"];
const DELETE_URL = {
    bon_kain: "/bon-kain/", bon_peralatan: "/bon-peralatan/", setor: "/setor-barang/",
    kasbon: "/kasbon/", setor_perbaikan: "/setor-perbaikan/", reject: "/reject/",
};
const DAY_NAMES = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const MONTH_NAMES = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function formatTanggalPanjang(dateStr) {
    if (!dateStr) return "-";
    const d = new Date(dateStr + "T00:00:00");
    if (isNaN(d.getTime())) return dateStr;
    try {
        return new Intl.DateTimeFormat("id-ID", {
            day: "2-digit", month: "long", year: "numeric", weekday: "long",
        }).format(d).replace(/^(\w+), (.+)$/, "$2 ($1)");
    } catch {
        const dd = String(d.getDate()).padStart(2, "0");
        return `${dd} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()} (${DAY_NAMES[d.getDay()]})`;
    }
}

function JurnalTab({ entries, onRowClick, vendorId, vendorNama, onPreview, onEdit, onDelete }) {
    // v1.8.10 — Filter tahun default "all" (SEMUA waktu). User backfill historis 2023+ bisa lihat semua
    // transaksi tanpa harus ubah filter periode dulu. Chip buttons tahun otomatis dari data yang ada.
    const [filterYear, setFilterYear] = useState("all");

    const yearCounts = useMemo(() => {
        const map = new Map();
        entries.forEach((e) => {
            const y = (e.tanggal || "").slice(0, 4);
            if (!y) return;
            map.set(y, (map.get(y) || 0) + 1);
        });
        return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0])); // tahun terbaru dulu
    }, [entries]);

    const filteredEntries = useMemo(() => {
        if (filterYear === "all") return entries;
        return entries.filter((e) => (e.tanggal || "").startsWith(filterYear));
    }, [entries, filterYear]);

    const groups = useMemo(() => {
        const map = new Map();
        filteredEntries.forEach((e) => {
            const key = e.tanggal || "unknown";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(e);
        });
        return Array.from(map.entries())
            .sort((a, b) => (b[0] || "").localeCompare(a[0] || ""))
            .map(([tanggal, items]) => {
                const totalNilai = items.reduce((s, e) => s + Math.max(e.debit || 0, e.kredit || 0), 0);
                // Hitung petugas dominan: nama dengan count terbanyak (dedup, maks 2 nama)
                const petugasCount = new Map();
                items.forEach((it) => {
                    const p = (it.petugas || "").trim() || "-";
                    petugasCount.set(p, (petugasCount.get(p) || 0) + 1);
                });
                const sorted = Array.from(petugasCount.entries()).sort((a, b) => b[1] - a[1]);
                const dominantPetugas = sorted[0]?.[0] || "-";
                let petugasLabel;
                if (sorted.length === 1) petugasLabel = sorted[0][0];
                else if (sorted.length === 2) petugasLabel = `${sorted[0][0]}, ${sorted[1][0]}`;
                else petugasLabel = `${sorted[0][0]}, ${sorted[1][0]} +${sorted.length - 2}`;
                return { tanggal, items, totalNilai, count: items.length, petugasLabel, dominantPetugas };
            });
    }, [filteredEntries]);

    const [expanded, setExpanded] = useState(new Set());
    // v1.8.3 fix: Re-expand newest date group on ANY entries change (add/delete),
    // not only on groups.length / groups[0].tanggal change. Prevents the common bug
    // where a user manually collapses today's group, saves a new setor on the same
    // date, and the newly-saved entry stays hidden (fueled "setor tidak tercatat"
    // complaint). We merge instead of overwrite so user's other expansions survive.
    useEffect(() => {
        if (groups.length > 0) {
            setExpanded((prev) => new Set([...prev, groups[0].tanggal]));
        }
    }, [entries.length, groups.length, groups[0]?.tanggal]);

    const toggle = (tgl) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(tgl)) next.delete(tgl); else next.add(tgl);
            return next;
        });
    };

    const cetakHarian = (tgl, e) => {
        e.stopPropagation();
        const nama = (vendorNama || "vendor").replace(/\s+/g, "_");
        onPreview?.({
            title: `Nota Harian ${vendorNama || ""} — ${tgl}`,
            url: `/vendors/${vendorId}/nota-harian/pdf?tanggal=${tgl}`,
            filename: `Nota_Harian_${nama}_${tgl}.pdf`,
        });
    };

    if (entries.length === 0) {
        return <div className="panel p-10 text-center text-slate-400 italic text-sm">Belum ada transaksi. Klik <span className="font-semibold text-teal-700">+ Catat Transaksi</span> untuk mulai.</div>;
    }

    const yearChips = (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-1" data-testid="jurnal-filter-tahun">
            <span className="text-xs font-medium text-slate-500">Filter tahun:</span>
            <button
                type="button"
                onClick={() => setFilterYear("all")}
                data-testid="jurnal-filter-all"
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${filterYear === "all" ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-700 border-slate-300 hover:border-teal-400"}`}
            >
                Semua ({entries.length})
            </button>
            {yearCounts.map(([y, c]) => (
                <button
                    key={y}
                    type="button"
                    onClick={() => setFilterYear(y)}
                    data-testid={`jurnal-filter-${y}`}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${filterYear === y ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-700 border-slate-300 hover:border-teal-400"}`}
                >
                    {y} ({c})
                </button>
            ))}
            {filterYear !== "all" && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 ml-auto" data-testid="jurnal-filter-banner">
                    Menampilkan {filteredEntries.length} dari {entries.length} transaksi · Filter: {filterYear}
                </span>
            )}
        </div>
    );

    return (
        <div className="jurnal-group-list" data-testid="jurnal-table">
            {yearChips}
            {groups.map((g) => {
                const isOpen = expanded.has(g.tanggal);
                return (
                    <div key={g.tanggal} className="jurnal-date-group">
                        <button
                            type="button"
                            className={`jurnal-date-header ${isOpen ? "open" : ""}`}
                            onClick={() => toggle(g.tanggal)}
                            data-testid={`jurnal-date-${g.tanggal}`}
                            aria-expanded={isOpen}
                        >
                            <span className="date-icon">{isOpen ? "▾" : "▸"}</span>
                            <span className="date-label">{formatTanggalPanjang(g.tanggal)}</span>
                            <span className="date-petugas" data-testid={`date-petugas-${g.tanggal}`}>
                                Petugas: <b>{g.petugasLabel}</b>
                            </span>
                            <span className="date-meta">
                                <span className="date-count">{g.count} transaksi</span>
                                <span className="date-total">{formatRupiah(g.totalNilai)}</span>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => cetakHarian(g.tanggal, e)}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") cetakHarian(g.tanggal, e); }}
                                    className="date-cetak-btn"
                                    data-testid={`cetak-harian-${g.tanggal}`}
                                    title={`Cetak semua transaksi tanggal ${g.tanggal}`}
                                >
                                    <Printer className="w-3.5 h-3.5" /> Cetak
                                </span>
                            </span>
                        </button>
                        {isOpen && (
                            <div className="jurnal-date-body">
                                {g.items.map((e, i) => (
                                    <NotaRow key={i} entry={e} onDetail={onRowClick} dominantPetugas={g.dominantPetugas} onEdit={onEdit} onDelete={onDelete} />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function NotaRow({ entry, onDetail, dominantPetugas, onEdit, onDelete }) {
    const judul = JENIS_LABEL_MAP[entry.jenis] || (entry.jenis || "").toUpperCase();
    const apiJenis = JENIS_API_MAP[entry.jenis];
    const isRijek = apiJenis === "reject" || entry.jenis === "Bon Rijek B";
    const ref = isRijek
        ? { jenis: "reject", id: entry.ref_id, nomor: entry.nomor, label: entry.jenis }
        : { jenis: apiJenis, id: entry.ref_id, nomor: entry.nomor };
    const canEdit = (EDITABLE.includes(apiJenis) || isRijek) && !entry.locked && entry.ref_id;
    const canDelete = (canEdit || apiJenis === "setor_perbaikan") && !entry.locked && entry.ref_id;
    const lines = (entry.keterangan || "").split("\n");
    const rincian = lines.length > 1 ? lines.slice(1) : lines;
    const nominal = (entry.kredit || 0) > 0 ? entry.kredit : entry.debit;
    const nominalLabel = (entry.kredit || 0) > 0 ? "Kredit" : (entry.debit || 0) > 0 ? "Beban" : "Nilai";
    const petugasTx = (entry.petugas || "").trim();
    const beda = petugasTx && dominantPetugas && petugasTx !== dominantPetugas;
    const stripMap = {
        "Pengambilan Kain": "strip-bon-kain",
        "Bon Peralatan": "strip-bon-peralatan",
        "Bon Rijek B": "strip-bon-kain",
        "Setor Barang Jadi": "strip-setor",
        "Kasbon": "strip-kasbon",
        "Rijek A": "strip-reject",
        "Rijek B": "strip-reject",
        "Setor Perbaikan": "strip-setor-perbaikan",
        "Pembayaran Totalan": "strip-pembayaran",
    };
    const stripCls = stripMap[entry.jenis] || "strip-totalan";
    return (
        <div className="jurnal-nota-row" data-testid={`nota-row-${entry.ref_id}`}>
            <div className={`nota-judul ${stripCls}`}>
                <span>{judul}
                    {entry.locked && <Lock className="w-3 h-3 inline-block ml-2 opacity-60" />}
                    {beda && (
                        <span className="nota-petugas-override" data-testid={`nota-petugas-override-${entry.ref_id}`}>
                            ·· {petugasTx}
                        </span>
                    )}
                </span>
                <span className="font-mono text-[10px] opacity-70">{entry.nomor || ""}</span>
            </div>
            <div className="nota-fields">
                {rincian.length > 0 && rincian[0] && (
                    <div className="nota-field">
                        <span className="nota-label">Rincian</span>
                        <div className="nota-value">
                            {rincian.map((ln, j) => <div key={j}>{ln}</div>)}
                        </div>
                    </div>
                )}
                {nominal > 0 && (
                    <div className="nota-field">
                        <span className="nota-label">{nominalLabel}</span>
                        <span className="nota-value nota-nominal">{formatRupiah(nominal)}</span>
                    </div>
                )}
            </div>
            <div className="nota-aksi">
                <button type="button" onClick={(e) => { e.stopPropagation(); onDetail(entry); }}
                        className="nota-btn-outline" data-testid={`nota-detail-${entry.ref_id}`}>
                    Lihat
                </button>
                {canEdit && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(ref); }}
                            className="act-pill act-pill-blue" data-testid={`nota-edit-${entry.ref_id}`}>
                        <Pencil className="w-3 h-3" /> Edit
                    </button>
                )}
                {canDelete && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(ref); }}
                            className="act-pill act-pill-red" data-testid={`nota-hapus-${entry.ref_id}`}>
                        <Trash2 className="w-3 h-3" /> Hapus
                    </button>
                )}
            </div>
        </div>
    );
}

function BonRijekTab({ bons, rejects, onReload, vendor, onEdit, onDelete }) {
    return (
        <div className="space-y-4">
            <div className="panel overflow-x-auto">
                <h3 className="p-3 font-semibold text-sm text-slate-700 border-b border-[var(--border)]">Bon Terbuka</h3>
                <table className="table-nota">
                    <thead><tr><th>Nomor</th><th>Tanggal</th><th>Jenis</th><th>Produk Ditugaskan</th><th className="text-right">Total</th><th className="text-right">Sisa</th><th>Status Finansial</th><th>Status Fisik</th><th></th></tr></thead>
                    <tbody>
                        {bons.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-4 italic text-xs">Tidak ada bon terbuka</td></tr>}
                        {bons.map((b) => {
                            const isRijekB = b.jenis_bon === "rijek_B_buyback";
                            const sisaRatio = b.sisa_bon / (b.total_nilai || 1);
                            const finStat = sisaRatio >= 0.99 ? ["Belum Lunas", "badge-oranye"] : sisaRatio < 0.01 ? ["Lunas", "badge-hijau"] : ["Dibayar Sebagian", "badge-biru"];
                            return (
                                <tr key={b.id}>
                                    <td className="font-mono text-xs">{b.nomor_bon}</td>
                                    <td>{formatDMY(b.tanggal)}</td>
                                    <td>{isRijekB ? "Bon Rijek B" : (b._jenis === "bon_kain" ? "Pengambilan" : "Peralatan")}</td>
                                    <td className="text-xs">{(b.produk_ditugaskan || []).map((p) => p.nama_produk_snapshot).join(", ") || "-"}</td>
                                    <td className="num">{formatRupiah(b.total_nilai)}</td>
                                    <td className="num num-debit">{formatRupiah(b.sisa_bon)}</td>
                                    <td><span className={`badge ${finStat[1]}`}>{finStat[0]}</span></td>
                                    <td>{isRijekB ? <span className="badge badge-biru">Dibuat Bon Baru</span> : <span className="badge badge-abu">—</span>}</td>
                                    <td>
                                        <div className="flex items-center gap-1.5">
                                            <NotaCetakBtn jenis={b._jenis} id={b.id} vendor={vendor} />
                                            {!b.locked && (
                                                <button onClick={() => onEdit(isRijekB ? { jenis: "reject", id: b.id, nomor: b.nomor_bon } : { jenis: b._jenis, id: b.id, nomor: b.nomor_bon })} data-testid={`bon-edit-${b.id}`} className="act-pill act-pill-blue"><Pencil className="w-3 h-3" /> Edit</button>
                                            )}
                                            {!b.locked && (
                                                <button onClick={() => onDelete(isRijekB
                                                    ? { jenis: "reject", id: b.id, nomor: b.nomor_bon, label: "Bon Rijek B" }
                                                    : { jenis: b._jenis, id: b.id, nomor: b.nomor_bon })}
                                                    data-testid={`bon-hapus-${b.id}`} className="act-pill act-pill-red"><Trash2 className="w-3 h-3" /> Hapus</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="panel overflow-x-auto">
                <h3 className="p-3 font-semibold text-sm text-slate-700 border-b border-[var(--border)]">Rijek Belum Selesai</h3>
                <table className="table-nota">
                    <thead><tr><th>Nomor</th><th>Tanggal</th><th>Setor Asal</th><th>Produk</th><th>Jenis</th><th className="text-right">Pcs</th><th>Status Finansial</th><th>Status Fisik</th><th>Aksi</th></tr></thead>
                    <tbody>
                        {rejects.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-4 italic text-xs">Tidak ada rijek belum selesai</td></tr>}
                        {rejects.map((r) => {
                            const isB = r.jenis === "B_tidak_bisa_diperbaiki";
                            return (
                                <tr key={r.id}>
                                    <td className="font-mono text-xs">{r.nomor_reject}</td>
                                    <td>{formatDMY(r.tanggal)}</td>
                                    <td className="font-mono text-xs">{r.setor_nomor_snapshot || r.setor_id?.slice(0, 8)}</td>
                                    <td className="text-xs">{r.nama_produk_snapshot}</td>
                                    <td><span className={`badge ${isB ? "badge-biru" : "badge-kuning"}`}>{isB ? "B: Buyback" : "A: Bisa Diperbaiki"}</span></td>
                                    <td className="num">{fmtQty(r.sisa_pcs ?? r.jumlah_pcs)} <span className="text-[10px] text-slate-400">{r.satuan === "meter" ? "mtr" : "pcs"}</span></td>
                                    <td><span className={`badge ${isB ? "badge-abu" : "badge-oranye"}`}>{isB ? "—" : "Belum Lunas"}</span></td>
                                    <td><span className={`badge ${isB ? "badge-biru" : "badge-kuning"}`}>{isB ? "Dibuat Bon Baru" : "Menunggu Perbaikan"}</span></td>
                                    <td>
                                        <div className="flex items-center gap-1.5">
                                            <UbahKeBBtn reject={r} onReload={onReload} />
                                            <NotaCetakBtn jenis="reject" id={r.id} vendor={vendor} />
                                            {!r.locked && (
                                                <button onClick={() => onEdit({ jenis: "reject", id: r.id, nomor: r.nomor_reject })}
                                                    data-testid={`reject-edit-${r.id}`} className="act-pill act-pill-blue"><Pencil className="w-3 h-3" /> Edit</button>
                                            )}
                                            {!r.locked && (
                                                <button onClick={() => onDelete({ jenis: "reject", id: r.id, nomor: r.nomor_reject, label: isB ? "Rijek B" : "Rijek A" })}
                                                    data-testid={`reject-hapus-${r.id}`} className="act-pill act-pill-red"><Trash2 className="w-3 h-3" /> Hapus</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const SETOR_STATUS = {
    belum: ["Belum di-Totalan", "badge-oranye"],
    sebagian: ["Sebagian di-Totalan", "badge-biru"],
    penuh: ["Terkunci", "badge-hijau"],
};

function YearChips({ items, dateKey, year, setYear, prefix }) {
    const years = [...new Set(items.map((x) => (x[dateKey] || "").slice(0, 4)).filter(Boolean))].sort().reverse();
    const chip = (val, label) => (
        <button key={val} onClick={() => setYear(val)} data-testid={`${prefix}-year-${val}`}
            className={`text-xs px-3 py-1 rounded-full border ${year === val ? "bg-[var(--apple-blue)] text-white border-[var(--apple-blue)]" : "bg-white border-[var(--border)] text-slate-600"}`}>{label}</button>
    );
    return (
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
            Filter tahun: {chip("semua", `Semua (${items.length})`)}
            {years.map((y) => chip(y, `${y} (${items.filter((x) => (x[dateKey] || "").startsWith(y)).length})`))}
        </div>
    );
}

function SetorStatusBadge({ s }) {
    const [lbl, cls] = SETOR_STATUS[s.status_totalan] || SETOR_STATUS.belum;
    return <span className={`badge ${cls}`} data-testid={`setor-status-${s.id}`}>{s.locked_by_totalan ? `${lbl} via ${s.nomor_totalan.join(", ")}` : lbl}</span>;
}

function SetorActions({ s, onEdit, onDelete, mobile }) {
    const ref = { jenis: "setor", id: s.id, nomor: s.nomor_setor };
    const sfx = mobile ? "-m" : "";
    const tip = s.locked_by_totalan ? `Sudah masuk ${s.nomor_totalan.join(", ")} — hapus Totalan dulu untuk mengubah` : undefined;
    return (
        <div className={mobile ? "totalan-card-actions" : "flex items-center gap-1.5"} title={tip}>
            <button disabled={s.locked_by_totalan} onClick={() => onEdit(ref)} data-testid={`setor-edit-${s.id}${sfx}`} className="act-pill act-pill-blue"><Pencil className="w-3 h-3" /> Edit</button>
            <button disabled={s.locked_by_totalan} onClick={() => onDelete(ref)} data-testid={`setor-hapus-${s.id}${sfx}`} className="act-pill act-pill-red"><Trash2 className="w-3 h-3" /> Hapus</button>
        </div>
    );
}

const setorProdukText = (s) => (s.items || []).map((it) => `${it.nama_produk_snapshot} · ${fmtQty(it.jumlah_pcs)} pcs`);

function SetoranTab({ setorans, onEdit, onDelete }) {
    const [year, setYear] = useState("semua");
    if (setorans.length === 0) return <div className="panel p-10 text-center italic text-slate-400 text-sm" data-testid="setoran-empty">Belum ada setoran untuk vendor ini.</div>;
    const list = year === "semua" ? setorans : setorans.filter((s) => (s.tanggal_setor || "").startsWith(year));
    return (
        <div className="space-y-3" data-testid="setoran-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <YearChips items={setorans} dateKey="tanggal_setor" year={year} setYear={setYear} prefix="setoran" />
                <div className="text-xs text-slate-500">Total nilai: <b className="num-kredit" data-testid="setoran-total">{formatRupiah(list.reduce((t, s) => t + (s.total_nilai || 0), 0))}</b></div>
            </div>
            <div className="panel overflow-x-auto hidden md:block">
                <table className="table-nota" data-testid="setoran-table">
                    <thead><tr><th>Nomor</th><th>Tanggal</th><th>Produk & Jumlah</th><th className="text-right">Total Nilai</th><th>Status</th><th>Alokasi ke Bon</th><th>Aksi</th></tr></thead>
                    <tbody>
                        {list.map((s) => (
                            <tr key={s.id} data-testid={`setor-row-${s.id}`}>
                                <td className="font-mono text-xs">{s.nomor_setor}</td>
                                <td>{formatDMY(s.tanggal_setor)}</td>
                                <td className="text-xs">{setorProdukText(s).map((t, i) => <div key={i}>{t}</div>)}</td>
                                <td className="num num-kredit">{formatRupiah(s.total_nilai)}</td>
                                <td><SetorStatusBadge s={s} /></td>
                                <td className="font-mono text-xs">{(s.bon_asal || []).join(", ") || "—"}</td>
                                <td><SetorActions s={s} onEdit={onEdit} onDelete={onDelete} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="md:hidden space-y-2">
                {list.map((s) => (
                    <div key={s.id} className="panel p-3" data-testid={`setor-card-${s.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div>
                                <div className="font-mono text-xs font-semibold">{s.nomor_setor}</div>
                                <div className="text-[11px] text-slate-500">{formatDMY(s.tanggal_setor)}{(s.bon_asal || []).length ? ` · dari ${s.bon_asal.join(", ")}` : ""}</div>
                            </div>
                            <SetorStatusBadge s={s} />
                        </div>
                        <div className="text-xs text-slate-700 space-y-0.5">{setorProdukText(s).map((t, i) => <div key={i}>{t}</div>)}</div>
                        <div className="text-sm font-semibold num-tabular num-kredit mt-1">{formatRupiah(s.total_nilai)}</div>
                        <SetorActions s={s} onEdit={onEdit} onDelete={onDelete} mobile />
                    </div>
                ))}
            </div>
        </div>
    );
}

const KASBON_STATUS = {
    belum_potong: ["Belum Potong", "badge-oranye"],
    potong_sebagian: ["Potong Sebagian", "badge-biru"],
    lunas: ["Lunas", "badge-hijau"],
};

function KasbonActions({ k, onEdit, onDelete, mobile }) {
    if (k.locked_by_totalan) {
        return <span className="text-[11px] text-slate-400 italic" title="Kasbon sudah dipotong Totalan — hapus Totalan dulu untuk mengubah">Terkunci Totalan</span>;
    }
    const ref = { jenis: "kasbon", id: k.id, nomor: k.nomor_kasbon };
    const sfx = mobile ? "-m" : "";
    return (
        <div className={mobile ? "totalan-card-actions" : "flex items-center gap-1.5"}>
            <button onClick={() => onEdit(ref)} data-testid={`kasbon-edit-${k.id}${sfx}`} className="act-pill act-pill-blue"><Pencil className="w-3 h-3" /> Edit</button>
            <button onClick={() => onDelete(ref)} data-testid={`kasbon-hapus-${k.id}${sfx}`} className="act-pill act-pill-red"><Trash2 className="w-3 h-3" /> Hapus</button>
        </div>
    );
}

function KasbonAktifList({ items, onEdit, onDelete }) {
    if (items.length === 0) return <div className="panel p-6 text-center italic text-slate-400 text-sm">Tidak ada kasbon aktif.</div>;
    return (
        <>
            <div className="panel overflow-x-auto hidden md:block">
                <table className="table-nota" data-testid="kasbon-aktif-table">
                    <thead><tr><th>Nomor</th><th>Tanggal</th><th className="text-right">Jumlah</th><th className="text-right">Sudah Dipotong</th><th className="text-right">Sisa</th><th>Alasan</th><th>Status</th><th>Aksi</th></tr></thead>
                    <tbody>
                        {items.map((k) => {
                            const [lbl, cls] = KASBON_STATUS[k.status_potong];
                            return (
                                <tr key={k.id} data-testid={`kasbon-row-${k.id}`}>
                                    <td className="font-mono text-xs">{k.nomor_kasbon}</td>
                                    <td>{formatDMY(k.tanggal)}</td>
                                    <td className="num">{formatRupiah(k.jumlah_kasbon)}</td>
                                    <td className="num num-kredit">{formatRupiah(k.sudah_dipotong)}</td>
                                    <td className="num num-debit font-semibold" data-testid={`kasbon-sisa-${k.id}`}>{formatRupiah(k.sisa)}</td>
                                    <td className="text-xs text-slate-600 max-w-[220px] truncate" title={k.alasan}>{k.alasan || "—"}</td>
                                    <td><span className={`badge ${cls}`}>{lbl}</span></td>
                                    <td><KasbonActions k={k} onEdit={onEdit} onDelete={onDelete} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="md:hidden space-y-2">
                {items.map((k) => {
                    const [lbl, cls] = KASBON_STATUS[k.status_potong];
                    return (
                        <div key={k.id} className="panel p-3" data-testid={`kasbon-card-${k.id}`}>
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div>
                                    <div className="font-mono text-xs font-semibold">{k.nomor_kasbon}</div>
                                    <div className="text-[11px] text-slate-500">{formatDMY(k.tanggal)}{k.alasan ? ` · ${k.alasan}` : ""}</div>
                                </div>
                                <span className={`badge ${cls}`}>{lbl}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div><div className="text-slate-400 text-[10px]">Jumlah</div><div className="font-semibold num-tabular">{formatRupiah(k.jumlah_kasbon)}</div></div>
                                <div><div className="text-slate-400 text-[10px]">Dipotong</div><div className="font-semibold num-tabular num-kredit">{formatRupiah(k.sudah_dipotong)}</div></div>
                                <div><div className="text-slate-400 text-[10px]">Sisa</div><div className="font-semibold num-tabular num-debit">{formatRupiah(k.sisa)}</div></div>
                            </div>
                            <KasbonActions k={k} onEdit={onEdit} onDelete={onDelete} mobile />
                        </div>
                    );
                })}
            </div>
        </>
    );
}

function KasbonLunasList({ items }) {
    const [open, setOpen] = useState(items.length <= 5);
    if (items.length === 0) return null;
    return (
        <div className="space-y-2">
            <button onClick={() => setOpen((o) => !o)} data-testid="kasbon-lunas-toggle" className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                <span className="text-xs">{open ? "▾" : "▸"}</span> Kasbon Lunas ({items.length})
            </button>
            {open && (
                <div className="panel divide-y divide-[var(--border)]" data-testid="kasbon-lunas-list">
                    {items.map((k) => (
                        <div key={k.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`kasbon-lunas-${k.id}`}>
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-xs">{k.nomor_kasbon}</span>
                                <span className="text-xs text-slate-500">{formatDMY(k.tanggal)}</span>
                                <span className="num-tabular font-medium">{formatRupiah(k.jumlah_kasbon)}</span>
                            </div>
                            <span className="badge badge-hijau">{k.nomor_totalan_pelunasan ? `Lunas via ${k.nomor_totalan_pelunasan}` : "Lunas"}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function KasbonTab({ kasbons, onEdit, onDelete }) {
    const [year, setYear] = useState("semua");
    const years = [...new Set(kasbons.map((k) => (k.tanggal || "").slice(0, 4)).filter(Boolean))].sort().reverse();
    const list = year === "semua" ? kasbons : kasbons.filter((k) => (k.tanggal || "").startsWith(year));
    const aktif = list.filter((k) => k.status_potong !== "lunas");
    const lunas = list.filter((k) => k.status_potong === "lunas");
    if (kasbons.length === 0) return <div className="panel p-10 text-center italic text-slate-400 text-sm" data-testid="kasbon-empty">Belum ada kasbon untuk vendor ini.</div>;
    const chip = (val, label) => (
        <button key={val} onClick={() => setYear(val)} data-testid={`kasbon-year-${val}`}
            className={`text-xs px-3 py-1 rounded-full border ${year === val ? "bg-[var(--apple-blue)] text-white border-[var(--apple-blue)]" : "bg-white border-[var(--border)] text-slate-600"}`}>{label}</button>
    );
    return (
        <div className="space-y-4" data-testid="kasbon-tab">
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
                Filter tahun: {chip("semua", `Semua (${kasbons.length})`)}
                {years.map((y) => chip(y, `${y} (${kasbons.filter((k) => (k.tanggal || "").startsWith(y)).length})`))}
            </div>
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-700">Kasbon Aktif ({aktif.length})</div>
                    <div className="text-xs text-slate-500">Total sisa: <b className="num-debit" data-testid="kasbon-aktif-total">{formatRupiah(aktif.reduce((s, k) => s + k.sisa, 0))}</b></div>
                </div>
                <KasbonAktifList items={aktif} onEdit={onEdit} onDelete={onDelete} />
            </div>
            <KasbonLunasList items={lunas} />
        </div>
    );
}

function totalanStatus(t) {
    const bayar = (t.pembayaran || []).reduce((s, p) => s + (p.jumlah || 0), 0);
    const hak = t.hak_vendor || 0;
    const sisa = Math.max(0, hak - bayar);
    const isDraft = t.status_konfirmasi === "draft";
    const isLunas = !isDraft && sisa <= 0.01;
    const [label, cls] = isDraft ? ["Draft", "badge-abu"]
        : isLunas ? ["Sudah Dibayar", "badge-hijau"]
        : bayar > 0 ? ["Dibayar Sebagian", "badge-biru"]
        : ["Belum Dibayar", "badge-oranye"];
    return { bayar, hak, sisa, isDraft, isLunas, label, cls };
}

function TotalanActions({ t, st, vendor, onAction, mobile }) {
    return (
        <div className={mobile ? "totalan-card-actions" : "flex items-center gap-1.5 flex-wrap"}>
            <NotaCetakBtn jenis="totalan" id={t.id} vendor={vendor} />
            {!st.isDraft && (
                <button
                    onClick={() => onAction({ kind: "toggle", t })}
                    disabled={st.hak <= 0.01}
                    data-testid={`totalan-toggle-${t.id}${mobile ? "-m" : ""}`}
                    title={st.isLunas ? "Status: Sudah Dibayar — klik untuk tandai Belum Dibayar" : "Status: Belum Dibayar — klik untuk tandai Sudah Dibayar"}
                    className={`act-pill ${st.isLunas ? "act-pill-green" : "act-pill-amber"}`}
                >
                    {st.isLunas ? "✓ Paid" : "↶ Unpaid"}
                </button>
            )}
            {!st.isDraft && !st.isLunas && st.hak > 0.01 && (
                <button onClick={() => onAction({ kind: "bayar", t })} data-testid={`totalan-bayar-${t.id}${mobile ? "-m" : ""}`} className="act-pill act-pill-blue">
                    <Wallet className="w-3 h-3" /> Catat Pembayaran
                </button>
            )}
            {(t.pembayaran || []).length > 0 && (
                <button onClick={() => onAction({ kind: "bayar", t })} data-testid={`totalan-riwayat-bayar-${t.id}${mobile ? "-m" : ""}`} className="act-pill act-pill-gray">
                    Riwayat ({t.pembayaran.length})
                </button>
            )}
            {st.isDraft && (
                <button onClick={() => onAction({ kind: "konfirmasi", t })} data-testid={`totalan-konfirmasi-${t.id}${mobile ? "-m" : ""}`} className="act-pill act-pill-blue">
                    Konfirmasi
                </button>
            )}
            <button onClick={() => onAction({ kind: "delete", t })} data-testid={`totalan-delete-${t.id}${mobile ? "-m" : ""}`} className="act-pill act-pill-red">
                <Trash2 className="w-3 h-3" /> Hapus
            </button>
        </div>
    );
}

const todayISO = () => new Date().toISOString().slice(0, 10);

function PembayaranModal({ t, onClose, onReload }) {
    const st = totalanStatus(t);
    const [jumlah, setJumlah] = useState("");
    const [tanggal, setTanggal] = useState(todayISO());
    const [metode, setMetode] = useState("Transfer");
    const [catatan, setCatatan] = useState("");
    const [saving, setSaving] = useState(false);
    const [delId, setDelId] = useState(null);
    const n = parseLocaleNum(jumlah);
    const over = n > st.sisa + 0.01;

    const submit = async () => {
        if (n <= 0) return toast.error("Jumlah pembayaran harus > 0");
        if (over) return toast.error(`Jumlah melebihi sisa ${formatRupiah(st.sisa)}`);
        if (!tanggal) return toast.error("Tanggal wajib diisi");
        setSaving(true);
        try {
            const { data } = await api.post(`/totalan/${t.id}/pembayaran`, { jumlah: n, tanggal, metode, catatan, idempotency_key: crypto.randomUUID?.() });
            toast.success(`Pembayaran ${formatRupiah(n)} berhasil dicatat. Sisa ${formatRupiah(data.sisa_bayar ?? st.sisa - n)}`);
            onClose(); onReload();
        } catch (e) { toast.error(errMsg(e, "Gagal mencatat pembayaran")); }
        finally { setSaving(false); }
    };
    const hapus = async (pid) => {
        setDelId(null);
        try { await api.delete(`/totalan/${t.id}/pembayaran/${pid}`); toast.success("Record pembayaran dihapus"); onReload(); }
        catch (e) { toast.error(errMsg(e, "Gagal menghapus pembayaran")); }
    };
    const inp = "w-full border border-[var(--border)] rounded px-3 py-2 text-sm bg-white";
    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center p-4" onClick={onClose}>
            <div className="panel p-5 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pembayaran-modal">
                <h3 className="font-bold mb-3">Catat Pembayaran Totalan {t.nomor_totalan}</h3>
                <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                    <div className="rounded-lg p-2 bg-slate-50"><div className="text-slate-500">Hak Vendor</div><div className="font-semibold num-tabular" data-testid="pembayaran-hak">{formatRupiah(st.hak)}</div></div>
                    <div className="rounded-lg p-2 bg-green-50"><div className="text-slate-500">Sudah Dibayar</div><div className="font-semibold num-tabular text-green-700" data-testid="pembayaran-dibayar">{formatRupiah(st.bayar)}</div></div>
                    <div className="rounded-lg p-2 bg-amber-50"><div className="text-slate-500">Sisa</div><div className="font-semibold num-tabular text-amber-700" data-testid="pembayaran-sisa">{formatRupiah(st.sisa)}</div></div>
                </div>
                {!st.isLunas && (
                    <div className="space-y-3 mb-4">
                        <div>
                            <label className="block text-xs font-medium mb-1">Jumlah Pembayaran <span className="text-red-500">*</span></label>
                            <div className="flex gap-2">
                                <input type="text" inputMode="decimal" value={jumlah} onChange={(e) => setJumlah(e.target.value)} placeholder="mis. 5000000" data-testid="pembayaran-jumlah" className={`${inp} ${over ? "border-red-400" : ""}`} />
                                <button type="button" onClick={() => setJumlah(String(st.sisa).replace(".", ","))} data-testid="pembayaran-isi-sisa" className="act-pill act-pill-gray">Lunasi</button>
                            </div>
                            <div className={`text-[11px] mt-1 ${over ? "text-red-600" : "text-slate-500"}`}>{over ? `Melebihi sisa ${formatRupiah(st.sisa)}` : `Maksimal ${formatRupiah(st.sisa)}`}{n > 0 && !over ? ` · Sisa setelah bayar ${formatRupiah(st.sisa - n)}` : ""}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs font-medium mb-1">Tanggal <span className="text-red-500">*</span></label>
                                <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} data-testid="pembayaran-tanggal" className={inp} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Metode</label>
                                <select value={metode} onChange={(e) => setMetode(e.target.value)} data-testid="pembayaran-metode" className={inp}>
                                    <option>Transfer</option><option>Tunai</option><option>Lainnya</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1">Catatan</label>
                            <input type="text" value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="pembayaran-catatan" className={inp} />
                        </div>
                    </div>
                )}
                {(t.pembayaran || []).length > 0 && (
                    <div className="mb-4" data-testid="pembayaran-riwayat">
                        <div className="text-xs font-semibold text-slate-600 mb-1">Riwayat Pembayaran</div>
                        <div className="border border-[var(--border)] rounded-lg divide-y divide-[var(--border)]">
                            {t.pembayaran.map((p) => (
                                <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`pembayaran-row-${p.id}`}>
                                    <div>
                                        <div className="font-semibold num-tabular">{formatRupiah(p.jumlah)}</div>
                                        <div className="text-slate-500">{formatDMY(p.tanggal)}{p.metode ? ` · ${p.metode}` : ""}{p.catatan ? ` · ${p.catatan}` : ""}</div>
                                    </div>
                                    {delId === p.id ? (
                                        <div className="flex gap-1">
                                            <button onClick={() => setDelId(null)} className="act-pill act-pill-gray">Batal</button>
                                            <button onClick={() => hapus(p.id)} data-testid={`pembayaran-hapus-confirm-${p.id}`} className="act-pill act-pill-red">Ya, hapus</button>
                                        </div>
                                    ) : (
                                        <button onClick={() => setDelId(p.id)} data-testid={`pembayaran-hapus-${p.id}`} className="act-pill act-pill-red"><Trash2 className="w-3 h-3" /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} data-testid="pembayaran-batal" className="px-3 py-1.5 border border-slate-300 rounded text-sm">{st.isLunas ? "Tutup" : "Batal"}</button>
                    {!st.isLunas && (
                        <button onClick={submit} disabled={saving || over} data-testid="pembayaran-simpan" className="px-4 py-1.5 rounded text-sm font-medium text-white bg-[#0071E3] hover:bg-[#0077ED] disabled:opacity-50">
                            {saving ? "Menyimpan…" : "Simpan Pembayaran"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function TotalanTab({ totalans, vendor, onReload }) {
    const [confirmAction, setConfirmAction] = useState(null); // {kind: "delete"|"toggle"|"konfirmasi", t}

    const run = async (fn, okMsg) => {
        try { await fn(); toast.success(okMsg); onReload(); }
        catch (e) { toast.error(`Gagal: ${e?.response?.data?.detail || e.message}`); }
        setConfirmAction(null);
    };
    const doToggle = (t) => run(() => api.post(`/totalan/${t.id}/toggle-payment`),
        totalanStatus(t).isLunas ? `${t.nomor_totalan} ditandai Belum Dibayar` : `${t.nomor_totalan} ditandai Sudah Dibayar`);
    const doDelete = (t) => run(() => api.delete(`/totalan/${t.id}`), `${t.nomor_totalan} dipindahkan ke Riwayat Terhapus`);
    const doKonfirmasi = (t) => run(() => api.post(`/totalan/${t.id}/konfirmasi`), `${t.nomor_totalan} dikonfirmasi`);

    if (totalans.length === 0) return <div className="panel p-10 text-center italic text-slate-400 text-sm">Belum ada totalan. Klik tombol <span className="font-semibold text-teal-700">Buat Totalan</span> untuk membuat.</div>;
    const cst = confirmAction ? totalanStatus(confirmAction.t) : null;
    const payLive = confirmAction?.kind === "bayar" ? (totalans.find((x) => x.id === confirmAction.t.id) || confirmAction.t) : null;
    return (
        <>
        <div className="panel overflow-x-auto hidden md:block">
            <table className="table-nota" data-testid="totalan-table">
                <thead><tr><th>Nomor</th><th>Tanggal</th><th className="text-right">Hak Vendor</th><th className="text-right">Dibayar</th><th className="text-right">Sisa</th><th>Status</th><th>Aksi</th></tr></thead>
                <tbody>
                    {totalans.map((t) => {
                        const st = totalanStatus(t);
                        return (
                            <tr key={t.id} data-testid={`totalan-row-${t.id}`}>
                                <td className="font-mono text-xs">{t.nomor_totalan}</td>
                                <td>{formatDMY(t.tanggal)}</td>
                                <td className="num">{formatRupiah(t.hak_vendor)}</td>
                                <td className="num num-kredit">{formatRupiah(st.bayar)}</td>
                                <td className="num num-debit">{formatRupiah(st.sisa)}</td>
                                <td><span className={`badge ${st.cls}`} data-testid={`totalan-status-${t.id}`}>{st.label}</span></td>
                                <td><TotalanActions t={t} st={st} vendor={vendor} onAction={setConfirmAction} /></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
        <div className="md:hidden space-y-2" data-testid="totalan-cards">
            {totalans.map((t) => {
                const st = totalanStatus(t);
                return (
                    <div key={t.id} className="panel p-3" data-testid={`totalan-card-${t.id}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <div>
                                <div className="font-mono text-xs font-semibold">{t.nomor_totalan}</div>
                                <div className="text-[11px] text-slate-500">{formatDMY(t.tanggal)}</div>
                            </div>
                            <span className={`badge ${st.cls}`} data-testid={`totalan-status-${t.id}-m`}>{st.label}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs mb-1">
                            <div><div className="text-slate-400 text-[10px]">Hak</div><div className="font-semibold num-tabular">{formatRupiah(t.hak_vendor)}</div></div>
                            <div><div className="text-slate-400 text-[10px]">Dibayar</div><div className="font-semibold num-tabular num-kredit">{formatRupiah(st.bayar)}</div></div>
                            <div><div className="text-slate-400 text-[10px]">Sisa</div><div className="font-semibold num-tabular num-debit">{formatRupiah(st.sisa)}</div></div>
                        </div>
                        <TotalanActions t={t} st={st} vendor={vendor} onAction={setConfirmAction} mobile />
                    </div>
                );
            })}
        </div>
        {payLive && <PembayaranModal t={payLive} onClose={() => setConfirmAction(null)} onReload={onReload} />}
        {confirmAction && confirmAction.kind !== "bayar" && (
            <div className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center p-4" onClick={() => setConfirmAction(null)}>
                <div className="panel p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="totalan-confirm-dialog">
                    <h3 className="font-bold mb-2">
                        {confirmAction.kind === "delete" && "Hapus Totalan?"}
                        {confirmAction.kind === "toggle" && "Ubah Status Pembayaran?"}
                        {confirmAction.kind === "konfirmasi" && "Konfirmasi Totalan Draft?"}
                    </h3>
                    <div className="text-xs text-slate-600 mb-4">
                        {confirmAction.kind === "delete" && `Totalan ${confirmAction.t.nomor_totalan} akan dipindahkan ke Riwayat Terhapus. Setoran/Bon/Kasbon yang di-reference akan unlock dan bisa dialokasikan ke Totalan lain. 30 hari bisa dipulihkan.`}
                        {confirmAction.kind === "toggle" && (cst.isLunas
                            ? `Totalan ${confirmAction.t.nomor_totalan} ditandai Belum Dibayar — record pembayaran dihapus.`
                            : `Totalan ${confirmAction.t.nomor_totalan} ditandai Sudah Dibayar — record pembayaran sejumlah ${formatRupiah(cst.sisa)} dibuat otomatis.`)}
                        {confirmAction.kind === "konfirmasi" && `Totalan ${confirmAction.t.nomor_totalan} status "Draft" akan diubah ke "Dikonfirmasi" (final, siap dibayar).`}
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setConfirmAction(null)} data-testid="totalan-confirm-cancel" className="px-3 py-1.5 border border-slate-300 rounded text-sm">Batal</button>
                        <button
                            onClick={() => {
                                if (confirmAction.kind === "delete") doDelete(confirmAction.t);
                                else if (confirmAction.kind === "toggle") doToggle(confirmAction.t);
                                else doKonfirmasi(confirmAction.t);
                            }}
                            data-testid="totalan-confirm-ok"
                            className={`px-3 py-1.5 rounded text-sm font-medium text-white ${confirmAction.kind === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-teal-600 hover:bg-teal-700"}`}
                        >
                            {confirmAction.kind === "delete" ? "Ya, Hapus" : "Ya, Lanjut"}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

export function NotaCetakBtn({ jenis, id, label, vendor }) {
    const [preview, setPreview] = useState(null);
    const open = () => {
        const title = `Nota ${jenis.replace("_", " ")} ${id.slice(0, 8)}`;
        setPreview({
            title, url: `/nota/${jenis}/${id}/pdf`,
            filename: `Nota_${jenis}_${id.slice(0, 8)}.pdf`,
            share: vendor ? {
                jenis, id,
                vendor_no_hp: vendor.no_hp || "",
                vendor_nama: vendor.nama || "",
                nota_kode: id.slice(0, 8),
            } : null,
        });
    };
    return (
        <>
            <button onClick={open} data-testid={`nota-${jenis}-${id}`} className={label ? "px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium text-sm inline-flex items-center gap-1.5" : "text-teal-600 hover:text-teal-800 p-1 inline-flex"} title="Cetak Nota">
                <Printer className="w-3.5 h-3.5" />{label && <span>{label}</span>}
            </button>
            <PreviewNotaModal open={!!preview} onClose={() => setPreview(null)}
                title={preview?.title || ""} url={preview?.url || ""} filename={preview?.filename || "nota.pdf"}
                share={preview?.share || null} />
        </>
    );
}

function UbahKeBBtn({ reject, onReload }) {
    const [open, setOpen] = useState(false);
    const [harga, setHarga] = useState("");
    const [alasan, setAlasan] = useState("");
    if (reject.jenis !== "A_bisa_diperbaiki") return null;
    const submit = async () => {
        if (!harga || Number(harga) <= 0) return toast.error("Harga manual per pcs wajib > 0");
        try {
            await api.post(`/reject/${reject.id}/ubah-ke-B`, { harga_pcs_manual: Number(harga), alasan });
            toast.success("Transisi A→B berhasil. Bon baru dibuat.");
            setOpen(false); onReload();
        } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    };
    return (
        <>
            <button onClick={() => setOpen(true)} data-testid={`ubah-b-${reject.id}`} className="text-xs text-blue-600 hover:underline mr-2">Ubah ke B</button>
            {open && (
                <div className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center p-4" onClick={() => setOpen(false)}>
                    <div className="panel p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-bold mb-3">Ubah Reject ke Jenis B (Buyback)</h3>
                        <div className="text-xs text-slate-500 mb-3">Sisa {reject.sisa_pcs} pcs akan jadi bon baru. Setor perbaikan sebelumnya tidak terpengaruh.</div>
                        <label className="block text-xs font-medium mb-1">Harga Manual per Pcs (wajib)</label>
                        <input type="text" inputMode="decimal" value={harga} onChange={(e) => setHarga(e.target.value)} data-testid="ubah-b-harga" placeholder="Rp per pcs (desimal OK)" className="w-full border border-[var(--border)] rounded px-3 py-2 text-sm mb-3" />
                        <label className="block text-xs font-medium mb-1">Alasan</label>
                        <textarea value={alasan} onChange={(e) => setAlasan(e.target.value)} className="w-full border border-[var(--border)] rounded px-3 py-2 text-sm mb-3" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setOpen(false)} className="px-3 py-1.5 border border-slate-300 rounded text-sm">Batal</button>
                            <button onClick={submit} data-testid="ubah-b-submit" className="px-3 py-1.5 bg-teal-600 text-white rounded text-sm font-medium">Simpan</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function formatDMY(iso) {
    if (!iso) return "-";
    const parts = iso.split("-");
    if (parts.length !== 3) return iso;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}
